import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ShoppingCart,
  Truck,
  ClipboardText,
  ChartLineUp,
  Users,
  Globe,
  CheckCircle,
  ArrowRight,
  Envelope,
  Phone,
  MapPin,
  Spinner,
  Play
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PublicLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    property_type: '',
    message: ''
  });

  // Check if user came from tour end screen
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('demo') === 'true') {
      setDemoDialogOpen(true);
      // Clear the URL param
      navigate('/', { replace: true });
    }
  }, [location, navigate]);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/demo-request`, formData);
      toast.success('Demo request submitted! We will contact you shortly.');
      setDemoDialogOpen(false);
      setFormData({ name: '', email: '', company_name: '', property_type: '', message: '' });
    } catch (error) {
      console.error('Demo request failed:', error);
      toast.error('Failed to submit demo request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: <ClipboardText size={32} weight="duotone" />,
      title: 'Requisition Management',
      description: 'Digital purchase requisitions with automated approval workflows'
    },
    {
      icon: <ShoppingCart size={32} weight="duotone" />,
      title: 'Purchase Orders',
      description: 'Create, issue, and track purchase orders from a single dashboard'
    },
    {
      icon: <Users size={32} weight="duotone" />,
      title: 'Vendor Portal',
      description: 'Dedicated portal for vendors to confirm orders and upload documents'
    },
    {
      icon: <Truck size={32} weight="duotone" />,
      title: 'Freight Tracking',
      description: 'Real-time shipment tracking with ETA updates and document management'
    },
    {
      icon: <ChartLineUp size={32} weight="duotone" />,
      title: 'Analytics & Reporting',
      description: 'Comprehensive reports on spending, delivery performance, and more'
    },
    {
      icon: <Globe size={32} weight="duotone" />,
      title: 'Multi-Currency Support',
      description: 'Handle transactions in USD, XCD, JMD, TTD and more Caribbean currencies'
    }
  ];

  const benefits = [
    'Reduce procurement cycle time by up to 60%',
    'Eliminate paper-based requisitions',
    'Real-time visibility across all purchase orders',
    'Automated vendor communication',
    'Complete audit trail for compliance',
    'Role-based access for your entire team'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3D1D72] to-[#5B2D9E] flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>H</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#3D1D72]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  HERChain Solutions
                </h1>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-[#3D1D72] hover:bg-[#3D1D72]/5 hidden sm:inline-flex"
                onClick={() => navigate('/about')}
                data-testid="nav-about-btn"
              >
                About
              </Button>
              <Button 
                variant="ghost" 
                className="text-[#3D1D72] hover:bg-[#3D1D72]/5 hidden sm:inline-flex"
                onClick={() => navigate('/pricing')}
                data-testid="nav-pricing-btn"
              >
                Pricing
              </Button>
              <Button 
                variant="ghost" 
                className="text-[#3D1D72] hover:bg-[#3D1D72]/5"
                onClick={() => navigate('/tour')}
                data-testid="nav-tour-btn"
              >
                <Play size={18} className="mr-2" />
                See How It Works
              </Button>
              <Button 
                className="bg-[#C9A84C] hover:bg-[#B8973D] text-white"
                onClick={() => setDemoDialogOpen(true)}
                data-testid="nav-demo-btn"
              >
                Request Demo
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A84C]/10 rounded-full text-[#C9A84C] text-sm font-medium mb-6">
              <Globe size={18} />
              Built for Caribbean Hospitality
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3D1D72] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Empowering Caribbean<br />Hospitality Procurement
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
              The complete procurement and supply chain platform designed specifically for Caribbean hotels, resorts, and hospitality groups. Track every order, every step.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                className="bg-[#3D1D72] hover:bg-[#2D1556] text-white px-8 py-6 text-lg"
                onClick={() => navigate('/tour')}
                data-testid="hero-tour-btn"
              >
                <Play size={22} className="mr-2" />
                See How It Works
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-[#C9A84C] text-[#C9A84C] px-8 py-6 text-lg hover:bg-[#C9A84C]/5"
                onClick={() => setDemoDialogOpen(true)}
                data-testid="hero-demo-btn"
              >
                Request a Demo
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#3D1D72]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-white mb-2">8</p>
              <p className="text-[#C9A84C]">Core Modules</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-2">4+</p>
              <p className="text-[#C9A84C]">Caribbean Currencies</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-2">8</p>
              <p className="text-[#C9A84C]">User Roles</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-2">100%</p>
              <p className="text-[#C9A84C]">Cloud Based</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3D1D72] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Everything You Need to Manage Procurement
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A complete suite of tools designed for the unique challenges of Caribbean hospitality procurement
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-[#3D1D72]/10 flex items-center justify-center text-[#3D1D72] mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#3D1D72] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3D1D72] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Why Caribbean Hospitality Leaders Choose HERChain
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />
                    </div>
                    <p className="text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#3D1D72] to-[#5B2D9E] rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Ready to Transform Your Procurement?
              </h3>
              <p className="text-white/80 mb-8">
                See how HERChain Solutions can streamline your procurement operations and give you complete visibility across your supply chain.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="bg-[#C9A84C] hover:bg-[#B8973D] text-white"
                  onClick={() => navigate('/tour')}
                >
                  <Play size={18} className="mr-2" />
                  Take the Tour
                </Button>
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => setDemoDialogOpen(true)}
                >
                  Request Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About the Founder Teaser */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="about-teaser-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-2">
              <p className="text-xs font-semibold tracking-widest text-[#C9A84C] uppercase mb-3">
                Built by Practitioners
              </p>
              <h2
                className="text-3xl sm:text-4xl font-bold text-[#3D1D72] leading-tight mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Built by someone who's actually done the job.
              </h2>
              <div className="w-12 h-1 bg-[#C9A84C]" />
            </div>
            <div className="md:col-span-3 space-y-4 text-slate-700 leading-relaxed">
              <p>
                HERChain was founded by <span className="font-semibold text-[#3D1D72]">Dahlia Lewis</span>, a Caribbean hospitality procurement professional with <span className="font-semibold">23+ years</span> of hands-on experience — including senior roles at <span className="font-semibold">Four Seasons Resort Nevis</span> and <span className="font-semibold">Necker Island</span>.
              </p>
              <p>
                She was on the original implementation team for Birchstreet at a major Caribbean hotel, then served as its system administrator. Every feature in HERChain exists because she felt the pain of not having it.
              </p>
              <Button
                variant="outline"
                className="border-2 border-[#3D1D72] text-[#3D1D72] hover:bg-[#3D1D72]/5 mt-2"
                onClick={() => navigate('/about')}
                data-testid="landing-about-read-more"
              >
                Read the full story
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8F5FF]" data-testid="pricing-teaser-section">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
            <span className="text-xs font-semibold tracking-widest text-[#8B7A2F] uppercase">
              Founding Partner Rates — Limited Time
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#3D1D72] mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto mb-10">
            Onboard now and lock in today's rates <span className="font-semibold italic">permanently</span>. Early partners are rewarded for coming in first.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <p className="text-sm font-semibold text-slate-500 tracking-wide uppercase mb-1">Boutique</p>
              <div className="text-3xl font-extrabold text-[#3D1D72]">$699<span className="text-base font-normal text-slate-500">/mo</span></div>
              <p className="text-xs text-slate-500 mt-2">1–2 outlets, 3 users</p>
            </div>
            <div className="bg-[#3D1D72] rounded-xl p-6 shadow-xl shadow-[#3D1D72]/20 sm:-translate-y-2 border-2 border-[#C9A84C] relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                Most Popular
              </div>
              <p className="text-sm font-semibold text-white/70 tracking-wide uppercase mb-1">Property</p>
              <div className="text-3xl font-extrabold text-white">$1,500<span className="text-base font-normal text-white/70">/mo</span></div>
              <p className="text-xs text-white/70 mt-2">Mid-size resorts, 10 users</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <p className="text-sm font-semibold text-slate-500 tracking-wide uppercase mb-1">Portfolio</p>
              <div className="text-3xl font-extrabold text-[#3D1D72]">$3,000<span className="text-base font-normal text-slate-500">/mo</span></div>
              <p className="text-xs text-slate-500 mt-2">Multi-property groups</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="lg"
            className="border-2 border-[#3D1D72] text-[#3D1D72] hover:bg-[#3D1D72]/5 px-8 py-6 text-base"
            onClick={() => navigate('/pricing')}
            data-testid="landing-pricing-see-plans"
          >
            See full plans & features
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#3D1D72] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            See HERChain in Action
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Take our interactive product tour to see how HERChain Solutions can transform your procurement operations
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              className="bg-[#3D1D72] hover:bg-[#2D1556] text-white px-8 py-6 text-lg"
              onClick={() => navigate('/tour')}
            >
              <Play size={22} className="mr-2" />
              See How It Works
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#3D1D72] text-[#3D1D72] px-8 py-6 text-lg hover:bg-[#3D1D72]/5"
              onClick={() => setDemoDialogOpen(true)}
            >
              Request a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3D1D72] text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <span className="text-white font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>H</span>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>HERChain Solutions</span>
              </div>
              <p className="text-white/70 max-w-md">
                Empowering Caribbean hospitality with modern procurement and supply chain solutions. Built specifically for the unique needs of resorts, hotels, and hospitality groups across the region.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-3 text-white/70">
                <div className="flex items-center gap-2">
                  <Envelope size={18} />
                  <span>support@herchainsolutions.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={18} />
                  <span>+1 (268) 555-0123</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin size={18} />
                  <span>Antigua, West Indies</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-white/70">
                <button 
                  onClick={() => navigate('/about')} 
                  className="block hover:text-white transition-colors"
                  data-testid="footer-about-link"
                >
                  About the Founder
                </button>
                <button 
                  onClick={() => navigate('/pricing')} 
                  className="block hover:text-white transition-colors"
                  data-testid="footer-pricing-link"
                >
                  Pricing
                </button>
                <button 
                  onClick={() => navigate('/tour')} 
                  className="block hover:text-white transition-colors"
                >
                  Product Tour
                </button>
                <button 
                  onClick={() => setDemoDialogOpen(true)} 
                  className="block hover:text-white transition-colors"
                >
                  Request Demo
                </button>
              </div>
            </div>
          </div>
          
          {/* Footer Bottom Section with Links */}
          <div className="border-t border-white/10 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/50 text-sm">
                &copy; {new Date().getFullYear()} HERChain Solutions. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a 
                  href="/privacy-policy" 
                  className="text-white/60 hover:text-white text-sm transition-colors"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </a>
                <a 
                  href="/terms-of-service" 
                  className="text-white/60 hover:text-white text-sm transition-colors"
                  data-testid="footer-terms-link"
                >
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Demo Request Dialog */}
      <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D1D72]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Request a Demo
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDemoRequest} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                required
                data-testid="demo-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Work Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@resort.com"
                required
                data-testid="demo-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company">Property/Company Name</Label>
              <Input
                id="company"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Sandals Resort"
                required
                data-testid="demo-company-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="property_type">Property Type</Label>
              <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                <SelectTrigger data-testid="demo-property-select">
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_resort">Single Resort</SelectItem>
                  <SelectItem value="hotel_group">Hotel Group</SelectItem>
                  <SelectItem value="boutique_hotel">Boutique Hotel</SelectItem>
                  <SelectItem value="restaurant_chain">Restaurant Chain</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us about your procurement challenges..."
                rows={3}
                data-testid="demo-message-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#3D1D72] hover:bg-[#2D1556]"
              disabled={submitting}
              data-testid="demo-submit-btn"
            >
              {submitting ? (
                <>
                  <Spinner size={18} className="animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicLandingPage;
